// src/Math/LinearGraphTool.js
import React, { useRef, useState, useEffect } from "react";
import drawGrid from "./drawGrid"; // your existing drawGrid.js

export default function LinearGraphTool({
  widthCells = 10,
  heightCells = 10,
  cellSize = 30,
}) {
  const canvasRef = useRef(null);
  const [points, setPoints] = useState([]);
  const [xVal, setXVal] = useState("");
  const [yVal, setYVal] = useState("");
  const [lineEq, setLineEq] = useState(null);

  // Redraw grid, points, and line every time points change
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    // 1) Draw grid (no points)
    drawGrid(canvas, { widthCells, heightCells, cellSize }, []);

    // 2) Plot each red point
    points.forEach(({ x, y }) => {
      const cx = x * cellSize;
      // flip Y so (0,0) is bottom-left
      const cy = canvas.height - y * cellSize;
      ctx.beginPath();
      ctx.arc(cx, cy, 5, 0, 2 * Math.PI);
      ctx.fillStyle = "red";
      ctx.fill();
      ctx.font = "12px sans-serif";
      ctx.fillText(`(${x},${y})`, cx + 6, cy - 6);
    });

    // 3) If ≥2 points, compute best-fit line through first two:
    if (points.length >= 2) {
      const [p1, p2] = points;
      const rise = p2.y - p1.y;
      const run = p2.x - p1.x;
      const m = rise / run;
      const b = p1.y - m * p1.x;
      setLineEq({ m, b });

      // draw the line across the whole width
      ctx.beginPath();
      const x0 = 0, y0 = b;
      const x1 = widthCells, y1 = m * x1 + b;
      const cx0 = x0 * cellSize, cy0 = canvas.height - y0 * cellSize;
      const cx1 = x1 * cellSize, cy1 = canvas.height - y1 * cellSize;
      ctx.strokeStyle = "blue";
      ctx.lineWidth = 2;
      ctx.moveTo(cx0, cy0);
      ctx.lineTo(cx1, cy1);
      ctx.stroke();
    } else {
      setLineEq(null);
    }
  }, [points, widthCells, heightCells, cellSize]);

  // add a new point from the input fields
  const addPoint = () => {
    const x = Number(xVal),
      y = Number(yVal);
    if (!isNaN(x) && !isNaN(y)) {
      setPoints((prev) => [...prev, { x, y }]);
      setXVal("");
      setYVal("");
    }
  };

  // clear everything
  const clearAll = () => {
    setPoints([]);
    setLineEq(null);
  };

  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
      <div>
        <canvas
          ref={canvasRef}
          width={widthCells * cellSize}
          height={heightCells * cellSize}
          style={{ border: "1px solid #444" }}
        />
        {lineEq && (
          <p style={{ marginTop: 8 }}>
            <strong>Line:</strong> y = {lineEq.m.toFixed(2)} x +{" "}
            {lineEq.b.toFixed(2)}
          </p>
        )}
      </div>

      <div style={{ minWidth: 180 }}>
        <h4>Plot Your Points</h4>
        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <input
            type="number"
            placeholder="x"
            value={xVal}
            onChange={(e) => setXVal(e.target.value)}
            style={{ width: 50 }}
          />
          <input
            type="number"
            placeholder="y"
            value={yVal}
            onChange={(e) => setYVal(e.target.value)}
            style={{ width: 50 }}
          />
        </div>
        <button onClick={addPoint} style={{ marginBottom: 8, width: "100%" }}>
          Plot Point
        </button>
        <button onClick={clearAll} style={{ width: "100%" }}>
          Clear All
        </button>
      </div>
    </div>
  );
}
